import { Controller } from "@hotwired/stimulus"

// Edit mode ("Anpassen" → "Fertig") for both dashboard grids. Read mode stays calm: no
// handles, no resize corners. The mode lives in `data-editing` on this element, so the
// templates switch with `group-data-[editing=true]/dashboard:` classes and a turbo-stream
// that replaces cells or the whole grid inherits it without any re-wiring.
//
// It also owns the undo stack. The group grid is replaced on every server-side change,
// which reconnects its grid controller — a stack kept there would be gone after the
// first resize. Entries are plain data ({ type: "order", ids } / { type: "width", widgetId,
// width } / { type: "height", widgetId, height }) and are applied by whichever grid
// controller is connected at the time, via the `dashboard-edit-mode:undo` event.
export default class extends Controller {
  static targets = ["undoButton", "announcer"]
  static values = { storageKey: String }

  connect() {
    this.history = []
    this.boundRecord = this.record.bind(this)
    this.boundRetract = this.retract.bind(this)
    this.boundAnnounce = this.announceEvent.bind(this)
    this.element.addEventListener("dashboard-grid:changed", this.boundRecord)
    this.element.addEventListener("dashboard-grid:failed", this.boundRetract)
    this.element.addEventListener("dashboard-grid:announce", this.boundAnnounce)
    // A reset reloads the page; the mode survives it so the user lands where they were.
    this.setEditing(this.readStoredMode(), { silent: true })
  }

  disconnect() {
    this.element.removeEventListener("dashboard-grid:changed", this.boundRecord)
    this.element.removeEventListener("dashboard-grid:failed", this.boundRetract)
    this.element.removeEventListener("dashboard-grid:announce", this.boundAnnounce)
  }

  get editing() {
    return this.element.dataset.editing === "true"
  }

  enter() {
    this.setEditing(true)
  }

  exit() {
    this.setEditing(false)
  }

  // Escape leaves edit mode — unless it is closing something on top of the dashboard: a
  // dialog, the add-widget panel, or the confirm overlay (which prevents the key's default).
  keydown(event) {
    if (event.key !== "Escape" || !this.editing || event.defaultPrevented) return
    if (document.querySelector("dialog[open]")) return
    if (this.element.querySelector("[data-dashboard-add-panel-target='overlay']:not(.hidden)"))
      return

    this.exit()
  }

  setEditing(editing, { silent = false } = {}) {
    this.element.dataset.editing = editing ? "true" : "false"
    this.storeMode(editing)
    if (!editing) this.history = []
    this.updateUndoButton()
    this.dispatch("change", { detail: { editing } })
    if (!silent && this.hasAnnouncerTarget) {
      this.announce(this.element.dataset[editing ? "enteredMessage" : "leftMessage"] || "")
    }
  }

  record(event) {
    if (!this.editing || !event.detail?.undo) return

    this.history.push(event.detail.undo)
    this.updateUndoButton()
  }

  // A save that failed was rolled back on screen, so its entry would undo nothing.
  retract(event) {
    this.history = this.history.filter((entry) => entry !== event.detail?.undo)
    this.updateUndoButton()
  }

  // After "Auf Standard zurücksetzen": the group dashboard swaps only its grid (no reload), and
  // the defaults come back with the same widget ids, so old entries would still apply to them.
  clearHistory(event) {
    if (event?.detail?.success === false) return

    this.history = []
    this.updateUndoButton()
  }

  undo() {
    const entry = this.history.pop()
    this.updateUndoButton()
    if (!entry) return

    this.dispatch("undo", { detail: entry })
  }

  // Cmd/Ctrl+Z while editing, as long as the focus is not in a text field. Shift+Z is redo
  // in most apps, so it is left alone rather than undoing one step too many.
  undoShortcut(event) {
    if (!this.editing || !(event.metaKey || event.ctrlKey) || event.shiftKey) return
    if (event.key.toLowerCase() !== "z") return
    if (event.target.closest("input, textarea, select, [contenteditable]")) return

    event.preventDefault()
    this.undo()
  }

  announceEvent(event) {
    this.announce(event.detail?.message || "")
  }

  announce(message) {
    if (!this.hasAnnouncerTarget) return

    // Clearing first makes screen readers repeat an identical message.
    this.announcerTarget.textContent = ""
    requestAnimationFrame(() => {
      this.announcerTarget.textContent = message
    })
  }

  updateUndoButton() {
    if (!this.hasUndoButtonTarget) return

    this.undoButtonTarget.disabled = this.history.length === 0
  }

  readStoredMode() {
    if (!this.storageKeyValue) return false
    try {
      return window.sessionStorage.getItem(this.storageKeyValue) === "true"
    } catch {
      return false
    }
  }

  storeMode(editing) {
    if (!this.storageKeyValue) return
    try {
      if (editing) {
        window.sessionStorage.setItem(this.storageKeyValue, "true")
      } else {
        window.sessionStorage.removeItem(this.storageKeyValue)
      }
    } catch {
      // Private windows may refuse storage; the mode then just resets on reload.
    }
  }
}
